import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { StatCard } from '../ui/StatCard';
import { StatusBanner } from '../shell/StatusBanner';
import { EffectRow } from '../shell/EffectRow';
import { isEffectBeneficialToOwner } from '../../utils/effectOwnerImpact';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { calculateRuneCost, getSpellByType } from '../../constants/spells';
import { COLORS, INPUT_STYLE } from '../../constants/theme';
import { isProtected } from '../../utils/protection';
import type { MagicShellProps } from '../../pages/Magic';
import type { SpellType } from '../../types/magic';

/** SF-148: relocated from Magic.tsx as this shell's sole remaining consumer post-migration —
 *  MobileMagicShell uses the MobileSciencesShell-precedent 44px inline style instead. */
const selectStyle = { ...INPUT_STYLE, border: `1px solid ${COLORS.inputBorder}` } as const;

/**
 * SF-148: thin wrapper around the existing (already desktop-appropriate) Magic JSX — no layout
 * redesign. Adds a real, focus-managed `<h1>`, replaces the old ad hoc protection `<div>` with
 * the shared `StatusBanner` primitive, and adds `<label htmlFor>`/`id` associations for the four
 * form controls (a pre-existing accessibility gap on desktop too, not mobile-specific).
 */
export function DesktopMagicShell({
  province,
  magicData,
  kingdomNumber,
  islandNumber,
  setKingdomNumber,
  setIslandNumber,
  handleChangeKingdom,
  isLoadingKingdom,
  kingdomMembers,
  selectedTarget,
  handleTargetChange,
  selectedTargetLabel,
  selectedTargetProtectionCountdown,
  spellGroups,
  selectedSpell,
  handleSpellChange,
  runeCost,
  isSubmitting,
  submitError,
  successMessage,
  castBlockedReason,
  handleCastSpell,
}: MagicShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  return (
    <div className="space-y-4">
      <h1 ref={h1Ref} tabIndex={-1} className="text-xl font-bold text-game-50 outline-none">
        Mystics
      </h1>

      {/* Stat Cards */}
      <div className="flex gap-3">
        <div className="w-36">
          <StatCard label="Wizards" value={formatNumber(magicData.wizards)} sub="Available" borderless />
        </div>
        <div className="w-36">
          <StatCard label="WPA" value={magicData.wpa} sub="Wizards per acre" borderless />
        </div>
        <div className="w-36">
          <StatCard label="Runes" value={formatNumber(magicData.currentRunes)} sub="Available" borderless />
        </div>
        <div className="w-36">
          <StatCard label="Mana" value={`${magicData.currentMana.toFixed(1)}%`} sub="Current mana" borderless />
        </div>
      </div>

      {/* Active Effects — SF-181 Component 4: own bordered section (no GlanceCard import into a
          page that doesn't otherwise use it), status-before-action, mirrors mobile's placement. */}
      <div
        className="rounded p-4 space-y-2"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.text.tertiary }}>
          Active Effects
        </div>
        {province.magic.activeEffects.length === 0 ? (
          <div className="text-sm" style={{ color: COLORS.text.tertiary }}>No active effects</div>
        ) : (
          province.magic.activeEffects.map((effect, idx) => (
            <EffectRow
              key={idx}
              name={formatEnumLabel(effect.effectType)}
              ticksRemaining={effect.ticksRemaining}
              indefinite={effect.indefinite}
              urgent={effect.ticksRemaining <= 1 && !effect.indefinite}
              beneficial={isEffectBeneficialToOwner(effect.effectType)}
            />
          ))
        )}
      </div>

      {/* Change Kingdom */}
      <div
        className="rounded p-4"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center gap-3">
          <label htmlFor="desktop-magic-kingdom-input" className="text-sm whitespace-nowrap" style={{ color: COLORS.text.secondary }}>
            Kingdom
          </label>
          <input
            id="desktop-magic-kingdom-input"
            type="number"
            inputMode="numeric"
            placeholder="14"
            value={kingdomNumber}
            onChange={(e) => setKingdomNumber(e.target.value)}
            onFocus={(e) => e.target.select()}
            className="w-16 rounded text-sm py-1.5 px-2"
            style={selectStyle}
          />
          <label htmlFor="desktop-magic-island-input" className="text-sm whitespace-nowrap" style={{ color: COLORS.text.secondary }}>
            Island
          </label>
          <input
            id="desktop-magic-island-input"
            type="number"
            inputMode="numeric"
            placeholder="40"
            value={islandNumber}
            onChange={(e) => setIslandNumber(e.target.value)}
            onFocus={(e) => e.target.select()}
            className="w-16 rounded text-sm py-1.5 px-2"
            style={selectStyle}
          />
          <Button onClick={handleChangeKingdom} disabled={isLoadingKingdom} variant="secondary">
            {isLoadingKingdom ? 'Loading...' : 'Change Kingdom'}
          </Button>
        </div>
      </div>

      {/* Target + Spell + Submit */}
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        {/* Target Selection */}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-3">
            <label htmlFor="desktop-magic-target-select" className="text-sm whitespace-nowrap" style={{ color: COLORS.text.secondary }}>
              Target
            </label>
            <select
              id="desktop-magic-target-select"
              value={selectedTarget}
              onChange={(e) => handleTargetChange(e.target.value)}
              className="flex-1 rounded text-sm py-1.5 px-2"
              style={selectStyle}
            >
              <option value="">Nobody (Self-Cast)</option>
              {kingdomMembers.map((member) => {
                const memberProtected = isProtected(member.protectionExpiresAtTick, province.sync.currentTick);
                // Not disabled: intel spells (Crystal Ball, Crystal Eye) are exempt from the
                // protection gate on the backend and must remain targetable here. Only the Cast
                // Spell button below gates on the *chosen* spell's actual exemption status.
                return (
                  <option key={member.provinceId} value={member.provinceId}>
                    {member.provinceName} ({member.location}){memberProtected ? ' (Protected)' : ''}
                  </option>
                );
              })}
            </select>
          </div>
          {selectedTargetProtectionCountdown && (
            <StatusBanner
              tone="protection"
              icon="🛡️"
              headline="Target is protected"
              detail={`${selectedTargetProtectionCountdown} — offensive spells cannot target this province yet. Intel spells (Crystal Ball, Crystal Eye) are exempt.`}
              live
            />
          )}
        </div>

        {/* Spell Selection */}
        <div className="p-4" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          <div className="flex items-center gap-3">
            <label htmlFor="desktop-magic-spell-select" className="text-sm whitespace-nowrap" style={{ color: COLORS.text.secondary }}>
              Spell
            </label>
            <select
              id="desktop-magic-spell-select"
              value={selectedSpell}
              onChange={(e) => handleSpellChange(e.target.value as SpellType | '')}
              className="flex-1 rounded text-sm py-1.5 px-2"
              style={selectStyle}
            >
              <option value="">-- Select a Spell --</option>
              {spellGroups.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.spells.map((spell) => {
                    const cost = calculateRuneCost(province.territory.totals.acres, spell);
                    return (
                      <option key={spell.type} value={spell.type}>
                        {spell.name} ({cost} Runes)
                      </option>
                    );
                  })}
                </optgroup>
              ))}
            </select>
          </div>
          {selectedSpell && (
            <p className="mt-2 text-sm" style={{ color: COLORS.text.tertiary }}>
              {getSpellByType(selectedSpell)?.description}
            </p>
          )}
          <div className="mt-2 flex items-center gap-3 text-sm">
            <span className="whitespace-nowrap">
              <span style={{ color: COLORS.text.tertiary }}>Rune Cost: </span>
              <span className="font-mono" style={{ color: runeCost > 0 ? COLORS.text.primary : COLORS.text.tertiary }}>
                {runeCost > 0 ? `${formatNumber(runeCost)} runes` : '—'}
              </span>
            </span>
            {runeCost > 0 && magicData.currentRunes < runeCost && (
              <span className="text-xs" style={{ color: COLORS.negative }}>
                Insufficient runes
              </span>
            )}
          </div>
        </div>

        {/* Submit Bar */}
        <div className="px-4 py-3 space-y-3" style={{ borderTop: `1px solid ${COLORS.border}` }}>
          {submitError && (
            <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
          )}
          {successMessage && (
            <div className="text-sm" style={{ color: COLORS.positive }}>{successMessage}</div>
          )}

          <div className="flex items-center gap-6 text-sm">
            <span className="whitespace-nowrap" style={{ color: COLORS.text.tertiary }}>
              Casting on: <span style={{ color: COLORS.text.primary }}>{selectedTargetLabel}</span>
            </span>
          </div>

          <div className="flex items-center">
            <div className="flex gap-2 ml-auto items-center">
              {castBlockedReason && (
                <span id="desktop-cast-blocked-reason" className="text-xs" style={{ color: COLORS.text.tertiary }}>
                  {castBlockedReason}
                </span>
              )}
              <Button
                onClick={handleCastSpell}
                isLoading={isSubmitting}
                disabled={!!castBlockedReason}
                aria-describedby={castBlockedReason ? 'desktop-cast-blocked-reason' : undefined}
              >
                Cast Spell
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
